/**
 * Video hero: pick the right clip, play it only when allowed and visible,
 * and keep a pause/play button in sync.
 *
 * - Visitors who prefer reduced motion see the still image; they can start the clip.
 * - The clip pauses while off screen (saves CPU and battery).
 * - A visitor's pause is respected when the clip scrolls back into view.
 */
export function initVideoHeroes() {
	const reduceMotion = window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;
	const isSmall = window.matchMedia( '(max-width: 639.98px)' ).matches;

	document.querySelectorAll( '[data-video-hero]' ).forEach( ( box ) => {
		const video = box.querySelector( 'video' );
		const button = box.querySelector( '.video-hero__toggle' );
		if ( ! video || ! video.dataset.src ) {
			return;
		}

		let wanted = ! reduceMotion; // What the visitor wants (button state).
		let visible = false;

		const label = button?.querySelector( '.show-for-sr' );
		const render = () => {
			const playing = ! video.paused;
			box.classList.toggle( 'is-playing', playing );
			if ( button && label ) {
				label.textContent = playing ? button.dataset.labelPause : button.dataset.labelPlay;
			}
		};

		const load = () => {
			if ( ! video.getAttribute( 'src' ) ) {
				video.src = ( isSmall && video.dataset.srcSmall ) || video.dataset.src;
			}
		};

		const sync = () => {
			if ( wanted && visible ) {
				load();
				const promise = video.play();
				if ( promise ) {
					promise.catch( () => render() ); // Autoplay blocked (e.g. data saver): keep the still image.
				}
			} else if ( ! video.paused ) {
				video.pause();
			}
		};

		video.addEventListener( 'play', render );
		video.addEventListener( 'pause', render );

		if ( button ) {
			button.hidden = false;
			button.addEventListener( 'click', () => {
				wanted = video.paused;
				sync();
				render();
			} );
		}

		if ( 'IntersectionObserver' in window ) {
			new IntersectionObserver(
				( entries ) => {
					visible = entries[ 0 ].isIntersecting;
					sync();
				},
				{ threshold: 0.25 }
			).observe( box );
		} else {
			visible = true;
			sync();
		}
		render();
	} );
}
